import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, ChevronDown, Brain, FileBarChart2 } from 'lucide-react';
import { ClassificationDetails, patternChip, type ClassificationResult, selectPosturePhotoTileClassification } from './ClassificationDetails';
import AnalysisPanels from './AnalysisPanels';
import type { AnalysisData, Model3D, CaptureSequence, CapturedImage, Settings } from './types';
import {
  POSTURE_ORIENTATION_STRATEGY_OPTIONS,
  postureStrategySupportsPractitionerObservations,
  resolvePostureOrientationStrategyId,
  type PostureOrientationStrategyId,
} from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { usePageHeaderVisibility } from '@/contexts/page-header-visibility-context';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { getLaymanPatternChipLabel } from '@/components/posture-analysis/self-assessment/classification-display-labels';
import { captureLacksLateralView, LATERAL_VIEW_REQUIRED_NOTICE } from '@/components/posture-analysis/lib/lateral-view-notice';

export type PractitionerObservations = {
  poochBelly?: boolean;
  flatGlutes?: boolean;
  ribFlare?: boolean;
  flatCervicalCurve?: boolean;
};

export interface AnalysisTilesProps {
  classification: ClassificationResult | null;
  analysisData: AnalysisData;
  model3D: Model3D | null;
  capturedImages: Record<CaptureSequence, CapturedImage | null>;
  settings: Settings;
  currentView: CaptureSequence | null;
  experimentalFeaturesEnabled?: boolean;
  /** When true both tiles start expanded (e.g. on desktop where there is room). */
  defaultExpanded?: boolean;
  postureOrientationStrategyId?: Settings['postureOrientationStrategyId'];
  onStrategyChange?: (id: PostureOrientationStrategyId) => void;
  practitionerObservations?: PractitionerObservations;
  onObservationsChange?: (obs: PractitionerObservations) => void;
  /**
   * `accordion` (default): two independently collapsible tiles (desktop / side panel).
   * `segmented`: a single panel with a `View Report | Posture` segmented control — used by the
   * mobile split view so the chosen drill-down is one tap away and always shown beside the Pic.
   */
  layout?: 'accordion' | 'segmented';
  /** Mobile split: allow collapsing the report tray to maximize photo space. */
  trayCollapsed?: boolean;
  onTrayCollapsedChange?: (collapsed: boolean) => void;
}

function buildReportSummary(
  analysisData: AnalysisData,
  currentView: CaptureSequence | null,
): { worstMetric: string | null } {
  const entries = currentView && analysisData[currentView]
    ? [[currentView, analysisData[currentView]] as const]
    : Object.entries(analysisData).filter(([, d]) => d && Object.keys(d).length > 0) as [string, NonNullable<AnalysisData[CaptureSequence]>][];

  let worstLabel = '';
  let worstValue = '';
  let worstNumeric = -Infinity;

  for (const [, data] of entries) {
    if (!data) continue;
    for (const [label, value] of Object.entries(data)) {
      const match = value.match(/^(\d+(?:\.\d+)?)/);
      if (!match) continue;
      const numeric = parseFloat(match[1]);
      if (numeric > worstNumeric) {
        worstNumeric = numeric;
        worstLabel = label;
        worstValue = value;
      }
    }
  }

  return { worstMetric: worstLabel ? `${worstLabel}: ${worstValue}` : null };
}

const OBSERVATION_ITEMS: {
  key: keyof PractitionerObservations;
  label: string;
  group: 'sb' | 'apt';
}[] = [
  { key: 'poochBelly', label: 'Pooch belly', group: 'sb' },
  { key: 'flatGlutes', label: 'Flat/absent glutes', group: 'sb' },
  { key: 'ribFlare', label: 'Rib flare', group: 'apt' },
  { key: 'flatCervicalCurve', label: 'Flat cervical curve', group: 'apt' },
];

const AnalysisTiles: React.FC<AnalysisTilesProps> = ({
  classification,
  analysisData,
  model3D,
  capturedImages,
  settings,
  currentView,
  experimentalFeaturesEnabled = false,
  defaultExpanded = true,
  postureOrientationStrategyId,
  onStrategyChange,
  practitionerObservations,
  onObservationsChange,
  layout = 'accordion',
  trayCollapsed = false,
  onTrayCollapsedChange,
}) => {
  const { isChromeHidden, isPostureExtendedChrome, isImmersiveChromeEnabled } = usePageHeaderVisibility();
  const { laymanMode } = usePostureMode();
  const chromeBottomDismissHidden =
    isImmersiveChromeEnabled && isPostureExtendedChrome && isChromeHidden;

  const [reportExpanded, setReportExpanded] = useState(defaultExpanded);
  const [classificationExpanded, setClassificationExpanded] = useState(defaultExpanded);
  const [activeSection, setActiveSection] = useState<'report' | 'posture'>('report');

  const photoTileClassification = classification
    ? selectPosturePhotoTileClassification(classification)
    : null;

  const hasViewReport = laymanMode || Object.values(analysisData).some(d => d && Object.keys(d).length > 0);
  const showClassification = true;
  const hasAnyContent = showClassification || hasViewReport;

  const activeStrategyId = resolvePostureOrientationStrategyId(
    postureOrientationStrategyId,
    experimentalFeaturesEnabled
  );
  // In segmented (mobile) mode there is no "Posture expanded" accordion; the Posture content is
  // visible whenever its segment is selected (falling back to Posture when there is no report).
  const segmentedSection: 'report' | 'posture' = hasViewReport ? activeSection : 'posture';
  const lateralNotice = captureLacksLateralView(capturedImages) ? (
    <p className="px-4 py-2 text-sm text-foreground">{LATERAL_VIEW_REQUIRED_NOTICE}</p>
  ) : null;
  const postureContentVisible =
    layout === 'segmented' ? segmentedSection === 'posture' : classificationExpanded;
  const showPractitionerObservationToggles =
    !laymanMode &&
    postureStrategySupportsPractitionerObservations(activeStrategyId) &&
    postureContentVisible;

  const classificationChips = photoTileClassification
    ? (() => {
        const chip = patternChip(photoTileClassification.pattern);
        const displayLabel = laymanMode
          ? getLaymanPatternChipLabel(photoTileClassification.pattern)
          : chip.label;
        const chips: Array<{ label: string; className: string }> = [{ ...chip, label: displayLabel }];
        if (photoTileClassification.lateralized && photoTileClassification.lateralized !== 'Balanced') {
          chips.push({ label: photoTileClassification.lateralized, className: 'bg-bg text-fg border-line' });
        }
        return chips;
      })()
    : null;

  const { worstMetric } = buildReportSummary(analysisData, currentView);

  // Shared content blocks — reused by both the accordion (desktop / side panel) and the
  // segmented (mobile split) layouts so the analysis rendering lives in exactly one place.
  const reportInner = (
    <AnalysisPanels
      analysisData={analysisData}
      model3D={model3D}
      capturedImages={capturedImages}
      settings={settings}
      currentView={currentView}
      variant="metricsOnly"
      experimentalFeaturesEnabled={experimentalFeaturesEnabled}
      postureOrientationStrategyId={activeStrategyId}
      suppressViewTitle={layout === 'segmented'}
      metricsDensity={layout === 'segmented' && laymanMode ? 'dense' : 'compact'}
    />
  );

  const postureInner = (
    <>
      {/* Strategy selector (experimental only) */}
      {!laymanMode && experimentalFeaturesEnabled && onStrategyChange && (
        <div
          className={cn(
            'flex flex-col gap-2 px-4 pt-3 pb-2',
            isPostureExtendedChrome && 'chrome-dismiss-transition',
            chromeBottomDismissHidden && 'chrome-dismiss-bottom-hidden',
          )}
        >
          <Label className="block text-sm text-foreground">
            Orientation
          </Label>
          <p className="text-sm text-muted-foreground">
            How Swayback, anterior pelvic tilt, and stacked are read from the captures.
          </p>
          <Select
            value={activeStrategyId}
            onValueChange={(v) => onStrategyChange(v as PostureOrientationStrategyId)}
          >
            <SelectTrigger className="h-11 w-full text-left text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {POSTURE_ORIENTATION_STRATEGY_OPTIONS.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} className="whitespace-normal text-sm" title={opt.label}>
                  {opt.shortLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Practitioner visual observations (supported orientation strategies) */}
      {showPractitionerObservationToggles && onObservationsChange && (
        <div className="px-4 pt-2 pb-3">
          <p className="text-[11px] text-muted mb-2">
            Visual findings observed by practitioner (optional)
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {OBSERVATION_ITEMS.map((item) => (
              <div key={item.key} className="flex items-center gap-2">
                <Switch
                  id={`obs-${item.key}`}
                  checked={practitionerObservations?.[item.key] ?? false}
                  onCheckedChange={(checked) =>
                    onObservationsChange({
                      ...practitionerObservations,
                      [item.key]: checked,
                    })
                  }
                  className="scale-75 origin-left"
                />
                <Label
                  htmlFor={`obs-${item.key}`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {item.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {photoTileClassification && (
        <div className="px-4 pb-4">
          <ClassificationDetails classification={photoTileClassification} showDetailSections />
        </div>
      )}
    </>
  );

  if (!hasAnyContent) {
    return (
      <p className="text-sm text-muted italic text-center py-6">
        No analysis data yet. Complete captures to see reports.
      </p>
    );
  }

  // Segmented (mobile split) layout: one card, a `View Report | Posture` toggle, and the chosen
  // panel scrolling inside the bottom region beside the pinned Pic.
  if (layout === 'segmented') {
    const segments: Array<{ id: 'report' | 'posture'; label: string }> = [
      ...(hasViewReport ? [{ id: 'report' as const, label: 'View Report' }] : []),
      { id: 'posture' as const, label: 'Posture' },
    ];
    const trayCollapsible = typeof onTrayCollapsedChange === 'function';
    const pinnedTrayScroll = (trayCollapsible && !trayCollapsed) || layout === 'segmented';
    return (
      <div className={cn(
        'flex flex-col rounded-xl border border-line bg-surface overflow-hidden',
        pinnedTrayScroll && 'min-h-[160px] flex-1 basis-0 h-full',
        trayCollapsed && 'shrink-0 flex-none',
      )}>
        {segments.length > 1 && (
          <div
            role="tablist"
            aria-label="Analysis view"
            className={cn(
              'flex shrink-0 items-center gap-1 border-b border-line p-1 overflow-x-auto scrollbar-hide flex-nowrap',
              laymanMode ? 'bg-surface' : 'bg-bg/30',
              laymanMode && layout === 'segmented' && 'py-0.5',
              isPostureExtendedChrome && 'chrome-dismiss-transition',
              chromeBottomDismissHidden && 'chrome-dismiss-bottom-hidden',
            )}
          >
            {hasViewReport && (
              <button
                role="tab"
                aria-selected={segmentedSection === 'report'}
                onClick={() => setActiveSection('report')}
                className={cn(
                  'shrink-0 rounded-lg pl-2 pr-3 font-medium transition-colors text-left',
                  laymanMode ? 'py-1 text-xs' : 'py-2 text-sm',
                  segmentedSection === 'report'
                    ? (laymanMode ? 'bg-bg text-fg shadow-sm' : 'bg-surface text-fg shadow-sm')
                    : 'text-muted hover:text-fg',
                )}
              >
                {laymanMode ? 'Measurements' : 'View Report'}
              </button>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-0.5">
              <button
                role="tab"
                aria-selected={segmentedSection === 'posture'}
                onClick={() => setActiveSection('posture')}
                className={cn(
                  'shrink-0 rounded-lg font-medium transition-colors',
                  laymanMode ? 'py-1 text-xs' : 'py-2 text-sm',
                  'inline-flex items-center gap-1.5 pl-2 pr-1',
                  segmentedSection === 'posture'
                    ? (laymanMode ? 'bg-bg text-fg shadow-sm' : 'bg-surface text-fg shadow-sm')
                    : 'text-muted hover:text-fg',
                )}
              >
                <span className="shrink-0">Posture</span>
                {classificationChips && classificationChips[0] && (
                  <Badge
                    variant="outline"
                    className={`shrink-0 border text-[10px] px-1.5 py-0 ${classificationChips[0].className}`}
                  >
                    {classificationChips[0].label}
                  </Badge>
                )}
              </button>
              {trayCollapsible && (
                <button
                  type="button"
                  className="shrink-0 rounded-lg p-1.5 text-muted hover:text-fg hover:bg-surface/80 touch-manipulation"
                  aria-label={trayCollapsed ? 'Expand report panel' : 'Minimize report panel'}
                  aria-expanded={!trayCollapsed}
                  onClick={() => onTrayCollapsedChange?.(!trayCollapsed)}
                >
                  {trayCollapsed ? (
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  )}
                </button>
              )}
            </div>
          </div>
        )}
        {!trayCollapsed && (
        <div
          data-testid="analysis-tray-scroll"
          className={cn(
            pinnedTrayScroll &&
              'h-0 min-h-0 flex-1 basis-0 overflow-y-auto overflow-x-hidden overscroll-y-contain touch-pan-y pb-4',
          )}
          style={pinnedTrayScroll ? { WebkitOverflowScrolling: 'touch' } : undefined}
        >
          {lateralNotice}
          {segmentedSection === 'report' ? reportInner : postureInner}
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 pb-4">
      {lateralNotice}
      {/* Tile 1: Posture Classification */}
      {showClassification && (
        <div className="rounded-xl border border-line bg-surface overflow-hidden">
          <button
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-bg/40 transition-colors"
            onClick={() => setClassificationExpanded(v => !v)}
            aria-expanded={classificationExpanded}
          >
            <Brain className="h-4 w-4 shrink-0 text-muted" />
            <span className="text-sm font-medium shrink-0">Posture</span>

            {!classificationExpanded && (
              <div className="flex-1 flex items-center gap-1.5 overflow-hidden">
                {classificationChips ? (
                  classificationChips.map((c, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className={`border text-xs px-2 py-0 shrink-0 ${c.className}`}
                    >
                      {c.label}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted italic truncate">Awaiting data…</span>
                )}
              </div>
            )}
            {classificationExpanded && <div className="flex-1" />}

            {classificationExpanded
              ? <ChevronUp className="h-4 w-4 shrink-0 text-muted" />
              : <ChevronDown className="h-4 w-4 shrink-0 text-muted" />}
          </button>

          {classificationExpanded && (
            <div className="border-t border-line">
              {postureInner}
            </div>
          )}
        </div>
      )}

      {/* Tile 2: View Report / Measurements */}
      {hasViewReport && (
        <div className="rounded-xl border border-line bg-surface overflow-hidden">
          <button
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-bg/40 transition-colors"
            onClick={() => setReportExpanded(v => !v)}
            aria-expanded={reportExpanded}
          >
            <FileBarChart2 className="h-4 w-4 shrink-0 text-muted" />
            <span className="text-sm font-medium shrink-0">{laymanMode ? 'Measurements' : 'View Report'}</span>

            {!reportExpanded && (
              <div className="flex-1 flex items-center gap-2 overflow-hidden min-w-0">
                {worstMetric && (
                  <span className="text-xs text-muted truncate">{worstMetric}</span>
                )}
              </div>
            )}
            {reportExpanded && <div className="flex-1" />}

            {reportExpanded
              ? <ChevronUp className="h-4 w-4 shrink-0 text-muted" />
              : <ChevronDown className="h-4 w-4 shrink-0 text-muted" />}
          </button>

          {reportExpanded && (
            <div className={cn("border-t border-line", laymanMode && "max-h-[260px] sm:max-h-[290px] overflow-y-auto p-1.5")}>
              {reportInner}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisTiles;
